import type { LocalRecord } from '@dovefiniscono/sync';
import { addDays, toISODate } from '../lib/dates';

export type FocusSession = LocalRecord<'focusSessions'>;
type Task = LocalRecord<'tasks'>;

// Focus figures for the timer page: minutes, streak and the last week. Points, levels and badges
// are the game's (features/game).

export const localDay = (instant: string) => toISODate(new Date(instant));

/** Session that is still running (at most one is expected). */
export const activeSession = (sessions: readonly FocusSession[]) =>
  sessions.find((session) => session.endedAt === null) ?? null;

export const sessionEnd = (session: FocusSession) =>
  new Date(new Date(session.startedAt).getTime() + session.plannedMinutes * 60_000);

export function computeProgress(
  sessions: readonly FocusSession[],
  tasks: readonly Task[],
  today: string,
) {
  const completed = sessions.filter((s) => s.completed);
  const doneTasks = tasks.filter((t) => t.completedAt !== null);

  const minutesByDay = new Map<string, number>();
  for (const session of completed) {
    const day = localDay(session.startedAt);
    minutesByDay.set(day, (minutesByDay.get(day) ?? 0) + session.plannedMinutes);
  }
  const activeDays = new Set(minutesByDay.keys());
  for (const task of doneTasks) if (task.completedAt) activeDays.add(localDay(task.completedAt));

  const totalMinutes = [...minutesByDay.values()].reduce((sum, m) => sum + m, 0);

  // A streak is not broken until the day is over: if today is still empty, count from yesterday.
  let streak = 0;
  for (
    let day = activeDays.has(today) ? today : addDays(today, -1);
    activeDays.has(day);
    day = addDays(day, -1)
  ) {
    streak++;
  }
  let best = 0;
  let run = 0;
  for (const day of [...activeDays].sort()) {
    run = activeDays.has(addDays(day, -1)) ? run + 1 : 1;
    best = Math.max(best, run);
  }

  const week = Array.from({ length: 7 }, (_, index) => {
    const day = addDays(today, index - 6);
    return { day, minutes: minutesByDay.get(day) ?? 0 };
  });
  return {
    totalMinutes,
    todayMinutes: minutesByDay.get(today) ?? 0,
    week,
    streak,
    bestStreak: best,
  };
}
